import uuid from 'uuid/v4';
import getEnvVar from '../utils/getEnvVar';
import { DD_GUEST_SERVICE_NAME, DD_HOST_SERVICE_NAME } from './constants';

const SPLASH_ANONYMOUS_ID = 'splash_anonymous_id';

/**
 * These are the common configuration options that need to match between RUM and Logs.
 * @see https://docs.datadoghq.com/real_user_monitoring/browser/setup/#initialization-parameters
 */
const commonOptions = {
    trackSessionAcrossSubdomains: true,
    useSecureSessionCookie: true,
    usePartitionedCrossSiteSessionCookie: true,
};

export const getEnvironmentName = () => {
    const hostName = window.location.hostname;

    let tld = '50';
    if (hostName.endsWith('.eu')) {
        tld = '54';
    }

    // We use `pr` as the default value to support customers who use their
    // own custom domains (i.e., not splashthat.com).
    let sdm = 'pr';
    if (__TESTING__) {
        sdm = 'ld';
    } else if (hostName.includes('splashrelease')) {
        sdm = 'sg';
    } else if (hostName.includes('splashdevelop')) {
        sdm = 'dv';
    }

    const environmentName = hostName.includes('splashlocal') ? 'splashlocal' : `${sdm}${tld}`;
    return {
        environmentName,
        isEu: tld === '54',
        isPr: environmentName !== 'splashlocal' && sdm === 'pr',
        isDv: environmentName === 'splashlocal' || sdm === 'dv',
    };
};

const datadogHostClientOptions = {
    service: DD_HOST_SERVICE_NAME,
    applicationId: getEnvVar('DD_HOST_APP_ID'),
    clientToken: getEnvVar('DD_HOST_CLIENT_TOKEN'),
};

const datadogGuestClientOptions = {
    service: DD_GUEST_SERVICE_NAME,
    applicationId: getEnvVar('DD_GUEST_APP_ID'),
    clientToken: getEnvVar('DD_GUEST_CLIENT_TOKEN'),
};

/**
 * This function returns the Datadog configuration based on whether the user is on the Host or Guest side.
 */
export const getDatadogLogsConfig = () => {
    const hostName = window.location.hostname;
    const isHostApp = hostName.startsWith('app.');
    const datadogClientOptions = isHostApp ? datadogHostClientOptions : datadogGuestClientOptions;
    const { environmentName: env } = getEnvironmentName();
    const version = getEnvVar('REACT_APP_VERSION');

    return {
        env,
        version,
        ...datadogClientOptions,
        ...commonOptions,
    };
};

/**
 * This function checks for an anonymous ID in the cookies based on the subdomain. If it exists, it returns the value;
 * otherwise, it creates and returns a new anonymous ID.
 */
export const getAnonymousId = () => {
    // Helper: parse cookies
    const getCookie = (name) => {
        const cookie = document.cookie
            .split(';')
            .map((c) => c.trim())
            .find((c) => c.startsWith(`${name}=`));
        return cookie ? decodeURIComponent(cookie.split('=')[1]) : null;
    };

    let anonymousId = getCookie(SPLASH_ANONYMOUS_ID);
    if (anonymousId) {
        return anonymousId;
    }

    // Generate new UUID
    anonymousId = uuid();

    // Expiration in 7 days
    const expires = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toUTCString();

    // Set cookie scoped to current subdomain only (no `domain` attribute!)
    document.cookie = `${SPLASH_ANONYMOUS_ID}=${anonymousId}; expires=${expires}; path=/; Secure; SameSite=Lax`;

    return anonymousId;
};
